import type { ParseDiagnostics, ParseResult, RawProfilePoint } from '@/types/profile';

/**
 * Parses raw text from a Mark Lumber CNC .C file into position/radius pairs.
 *
 * Format contract (from the machine export):
 *   Position ; Radius
 *   0.000 ; 12.54
 *   15.500 ; 12.68
 *
 * This parser is intentionally defensive because .C files come straight off
 * shop-floor CNC controllers and are never hand-validated:
 *  - Delimiter may be `;`, `,`, tab, or run(s) of whitespace.
 *  - Decimal separator may be `.` or `,` (some controllers are locale-aware).
 *  - Blank lines, comment-ish lines, and malformed rows must be skipped, not
 *    thrown away silently — every skip is recorded in diagnostics so the
 *    Status Console can show the operator exactly what happened.
 */

const CANDIDATE_DELIMITERS: Array<{
  id: ParseDiagnostics['delimiter'];
  pattern: RegExp;
}> = [
  { id: 'semicolon', pattern: /;/ },
  { id: 'tab', pattern: /\t/ },
  { id: 'whitespace', pattern: /\s{1,}/ },
  { id: 'comma', pattern: /,/ },
];

/** A line is a plausible data row if, after splitting, it yields exactly two numeric-ish fields. */
function looksNumeric(field: string): boolean {
  return /^-?\d+([.,]\d+)?$/.test(field.trim());
}

/**
 * Detects which delimiter the file uses by scoring each candidate against
 * every non-empty line and picking the one that produces the most
 * "two numeric fields" rows. Semicolon is checked first because it's the
 * documented Mark Lumber format, so ties favor it.
 */
function detectDelimiter(lines: string[]): ParseDiagnostics['delimiter'] {
  const nonEmpty = lines.map((l) => l.trim()).filter(Boolean);
  let best: { id: ParseDiagnostics['delimiter']; score: number } = {
    id: 'semicolon',
    score: -1,
  };

  for (const candidate of CANDIDATE_DELIMITERS) {
    // Comma is ambiguous with decimal-comma notation, so only consider it as
    // a delimiter if splitting on it still yields two numeric-looking fields
    // for the *majority* of lines — otherwise it's almost certainly being
    // used as a decimal separator, not a field separator.
    let score = 0;
    for (const line of nonEmpty) {
      const parts = line.split(candidate.pattern).map((p) => p.trim()).filter(Boolean);
      if (parts.length === 2 && looksNumeric(parts[0]) && looksNumeric(parts[1])) {
        score += 1;
      }
    }
    if (score > best.score) {
      best = { id: candidate.id, score };
    }
  }

  return best.id;
}

function delimiterToSplitter(id: ParseDiagnostics['delimiter']): RegExp {
  switch (id) {
    case 'semicolon':
      return /;/;
    case 'tab':
      return /\t+/;
    case 'whitespace':
      return /\s+/;
    case 'comma':
      return /,/;
  }
}

/** Detects whether numbers use `.` or `,` as the decimal separator. */
function detectDecimalStyle(
  rows: string[][],
  delimiter: ParseDiagnostics['delimiter'],
): ParseDiagnostics['decimalStyle'] {
  // If comma is the field delimiter, decimal style must be point (otherwise
  // we couldn't have split on comma at all).
  if (delimiter === 'comma') return 'point';

  let commaDecimals = 0;
  let pointDecimals = 0;
  for (const row of rows) {
    for (const field of row) {
      if (/^-?\d+,\d+$/.test(field)) commaDecimals += 1;
      if (/^-?\d+\.\d+$/.test(field)) pointDecimals += 1;
    }
  }
  return commaDecimals > pointDecimals ? 'comma' : 'point';
}

function parseNumber(field: string, decimalStyle: ParseDiagnostics['decimalStyle']): number {
  const normalized = decimalStyle === 'comma' ? field.replace(',', '.') : field;
  return Number.parseFloat(normalized);
}

export function parseCFile(fileText: string): ParseResult {
  // Normalize line endings, strip BOM.
  const cleanText = fileText.replace(/^\uFEFF/, '').replace(/\r\n?/g, '\n');
  const lines = cleanText.split('\n');

  const delimiter = detectDelimiter(lines);
  const splitter = delimiterToSplitter(delimiter);

  // First pass: split every non-empty line so we can detect decimal style
  // from the whole file, not just the header.
  const candidateRows: Array<{ lineNumber: number; parts: string[] }> = [];
  lines.forEach((raw, i) => {
    const trimmed = raw.trim();
    if (!trimmed) return; // ignore empty lines
    const parts = trimmed.split(splitter).map((p) => p.trim()).filter(Boolean);
    candidateRows.push({ lineNumber: i + 1, parts });
  });

  const decimalStyle = detectDecimalStyle(
    candidateRows.map((r) => r.parts),
    delimiter,
  );

  const points: RawProfilePoint[] = [];
  const skippedLineNumbers: number[] = [];
  let parsedRows = 0;

  for (const row of candidateRows) {
    if (row.parts.length !== 2) {
      skippedLineNumbers.push(row.lineNumber);
      continue;
    }
    const [posField, radField] = row.parts;
    if (!/^-?\d+([.,]\d+)?$/.test(posField)) {
      skippedLineNumbers.push(row.lineNumber);
      continue;
    }
    if (!/^-?\d+([.,]\d+)?$/.test(radField)) {
      skippedLineNumbers.push(row.lineNumber);
      continue;
    }

    const position = parseNumber(posField, decimalStyle);
    const radius = parseNumber(radField, decimalStyle);

    if (!Number.isFinite(position) || !Number.isFinite(radius)) {
      skippedLineNumbers.push(row.lineNumber);
      continue;
    }

    points.push({ index: points.length, position, radius });
    parsedRows += 1;
  }

  const diagnostics: ParseDiagnostics = {
    delimiter,
    decimalStyle,
    totalLines: lines.length,
    parsedRows,
    skippedRows: skippedLineNumbers.length,
    skippedLineNumbers,
  };

  return { points, diagnostics };
}
